import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem, Radio, Typography, Grid, debounce } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import { Button, DatePicker, Dropdown, LgSpace, MoreOptionsMenu, Section, SmSpace, TextField } from 'm2m.components';
import { localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import moment, { Moment } from 'moment';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { tipoCertidaoOptions } from 'common/genericOptions';
import {
  BloqueioResponse,
  CertidaoInadimplenciaParams,
  EmissaoStatus,
  FuncaoParticipante,
  InstrumentoFinanceiroApiResponse,
  InstrumentosFinanceirosFilter,
  InvestidorCertidaoResponse,
  InvestidorInstrumentoFinanceiroResponse,
  InvestidorResponse,
  ParticipanteResponse,
  RegistroStatus,
  TipoCertidao,
} from 'common/genericTypes';
import { getInstrumentoFinanceiroShortDescription } from 'common/genericDescriptions';
import { addCpfCnpjMask } from 'utils/mask';
import InvestidorAutoComplete from 'components/operacoes/investidorAutoComplete';
import DetalhesInvestidor from 'pages/investidores/detalhes/detalhesInvestidor';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { getGravameGridColumns } from 'utils/certidoes/gravameGrid';
import { getTitularidadeGridColumns } from 'utils/certidoes/titularidadeGrid';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import { getInadimplenciaColumns } from 'utils/certidoes/inadimplenciaGrid';
import { formatInstrumentoCurrency } from 'utils/instrumentoFinanceiroUtils';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import { CpfCnpjMask } from 'components/masks';

const Certidoes: React.FC = () => {

  const [tipo, setTipo] = useState<TipoCertidao | undefined>(undefined);
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [investidorInstrumentos, setInvestidorInstrumentos] = useState<Array<InvestidorInstrumentoFinanceiroResponse> | null>([]);
  const [dataReferencia, setDataReferencia] = useState<Moment>();
  const [investidorId, setInvestidorId] = useState<string>();
  const [investidorCpfCnpj, setInvestidorCpfCnpj] = useState<string>();
  const [localCpfCnpj, setLocalCpfCnpj] = useState<string>('');
  const [investidoresAtivos, setInvestidoresAtivos] = useState<Array<InvestidorCertidaoResponse>>([]);
  const [allInvestidores, setAllInvestidores] = useState<Array<InvestidorResponse>>([]);
  const [selectedInvestidor, setSelectedInvestidor] = useState<InvestidorResponse | null>(null);
  const [investidoresOfertaPrimaria, setInvestidoresOfertaPrimaria] = useState<Array<InvestidorCertidaoResponse>>([]);
  const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentos);
  const postInstrumentosPaginatedApi = useSelector((state: RootState) => state.instrumentoApi.postInstrumentosPaginated);
  const [page, setPage] = useState<number>(0);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [instrumentosFinanceirosFilter, setInstrumentosFinanceirosFilter] = useState<Array<InstrumentosFinanceirosFilter>>([]);
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [emissor, setEmissor] = useState<ParticipanteResponse | null>(null);
  const [emissorId, setEmissorId] = useState<string | null>(null);
  const usuario = useSelector((state: RootState) => state.auth.usuario);
  const [emissores, setEmissores] = useState<Array<ParticipanteResponse>>([]);
  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const getInvestidorInstrumentosApi = useSelector((state: RootState) => state.investidorApi.getInvestidorInstrumentos);
  const getInvestidoresApi = useSelector((state: RootState) => state.instrumentoApi.getInvestidores);
  const investidoresList = useSelector((state: RootState) => state.investidorApi.getInvestidores);
  const getInvestidoresEscrituradorApi = useSelector((state: RootState) => state.investidorApi.getInvestidoresEscriturador);
  const getTermoEncerramento = useSelector((state: RootState) => state.instrumentoApi.getTermoEncerramento);
  const dispatch = useDispatch();
  const history = useHistory();
  const { getBloqueios: getBloqueiosApi } = useSelector((state: RootState) => state.investidorApi);
  const { postCertidaoGravame, postCertidaoTitularidade, postCertidaoInadimplencia } = useSelector((state: RootState) => state.investidorApi);
  const [bloqueios, setBloqueios] = useState<Array<BloqueioResponse>>([]);
  const [botaoAtivar, setBotaoAtivarOuInativar] = useState<boolean>(false);
  const [listSelected, setListSelected] = useState<Array<string>>([]);
  const gravameGridColumns = getGravameGridColumns();
  const titularidadeGridColumns = getTitularidadeGridColumns();
  const inadimplenciaColumns = getInadimplenciaColumns();

  const getInstrumentos = useCallback(async (emissor: string | null) => {
    try {
      const result = await postInstrumentosPaginatedApi(
        dispatch,
        {
          page: page + 1,
          pageSize,
          linkOperator: linkOperator,
          registroStatus: [RegistroStatus.Aprovado],
          instrumentosFinanceirosFilter: instrumentosFinanceirosFilter,
        },
        true,
        emissor,
        null,
        null,
        null,
        usuario?.participante?.funcao === FuncaoParticipante.Escriturador ? usuario.participante.id : undefined,
      );
      setInstrumentos(result.result);
      setPageCount(result.totalCount);
    } catch { }
  }, [instrumentosFinanceirosFilter, pageSize, page, linkOperator]);

  const handleEmissorChange = (value: ParticipanteResponse | null) => {
    if (value?.id !== undefined) {
      setEmissor(value);
      setEmissorId(value.id);
      getInstrumentos(value.id);
    } else {
      setEmissorId(null);
    };
  }

  const getParticipantes = useCallback(async () => {
    try {
      const result = await getParticipantesApi(
        dispatch,
        {
          funcoes: [FuncaoParticipante.Emissor],
          status: RegistroStatus.Aprovado,
          suspenso: false
        },
        true);
      setEmissores(result);
    } catch (error) { }
  }, [dispatch]);

  const handleChangeTipo = (value: string) => {
    const newTipo = Object.values(TipoCertidao).find((tipo) => tipo === value);
    setTipo(newTipo);
    updateTipoCertidao(newTipo);
    setInstrumento(null);
    setSelectedInvestidor(null);
    setInvestidorId(undefined);
    setInvestidorCpfCnpj('');
    setLocalCpfCnpj('');
  };

  const handleChangeInvestidor = (investidor: InvestidorCertidaoResponse) => {
    setInvestidorId(investidor.investidorId);
  };

  const updateTipoCertidao = useCallback(async (tipoCertidao: TipoCertidao | undefined) => {
    if (!tipoCertidao) return;
    let request;
    let result: InstrumentoFinanceiroApiResponse[] | null = null;
    let resultGravame: BloqueioResponse[] | null = null;

    switch (tipoCertidao) {
      case TipoCertidao.CertidaoTitularidade:
        getAllInvestidores();
        getInvestidorInstrumentos();
        break;
      case TipoCertidao.TermoEncerramento:
        try {
          request = { emissaoStatus: [EmissaoStatus.Emitido, EmissaoStatus.ParcialmenteEmitido], encerrado: true, registroStatus: [RegistroStatus.Aprovado] };

          if (usuario?.participante?.funcao === FuncaoParticipante.Escriturador) {
            request = { ...request, escrituradorId: usuario?.participante?.id };
          }

          result = await getInstrumentosApi(dispatch, request, true);
          if (result && result.length > 0) {
            setInstrumentos(result);
            setInstrumento(result[0]);
          }
        } catch (error) {
          console.error("Error fetching TermoEncerramento instruments:", error);
        }
        break;
      case TipoCertidao.TermoEncerramento:
        try {
          request = { emissaoStatus: [EmissaoStatus.Emitido, EmissaoStatus.ParcialmenteEmitido], encerrado: true, registroStatus: [RegistroStatus.Aprovado] };

          if (usuario?.participante?.funcao === FuncaoParticipante.Emissor) {
            request = { ...request, escrituradorId: usuario?.participante?.id };
          }

          result = await getInstrumentosApi(dispatch, request, true);

          if (result && result.length > 0) {
            setInstrumentos(result);
            setInstrumento(result[0]);
          }
        } catch (error) {
          console.error("Error fetching TermoEncerramento instruments:", error);
        }
        break;

      case TipoCertidao.Gravame:
        try {
          resultGravame = await getBloqueiosApi(dispatch, true);
        } catch (error) { }
        break;
      case TipoCertidao.ExtratoMovimentacoes:
        try {
          getAllInvestidores();
        } catch (error) { }
        break;
      case TipoCertidao.CertidaoInadimplencia:
        try {
          if (usuario?.participante.funcao === FuncaoParticipante.Emissor) {
            setEmissorId(usuario?.participante.id);
            getInstrumentos(usuario?.participante.id);
          } else {
            getParticipantes();
          }
        } catch (error) { }
        break;

      default:
        return;
    }
    setBloqueios(resultGravame ?? []);
  }, []);

  const getInvestidorInstrumentos = useCallback(async () => {
    try {
      if (!dataReferencia?.isValid() || !selectedInvestidor?.cpfCnpj) {
        return;
      }

      setInvestidorInstrumentos(null);

      let result: Array<InvestidorInstrumentoFinanceiroResponse> = [];
      for (let retries = 0; retries < 2; retries++) {
        try {
          result = await getInvestidorInstrumentosApi(dispatch, selectedInvestidor?.cpfCnpj, { dataReferencia: dataReferencia.format('yyyy-MM-DD') }, true);
          break;
        } catch (error) { }
      }

      setInvestidorInstrumentos(result ?? null);
    } catch (error) {
      setInvestidorInstrumentos([]);
    }
  }, [dataReferencia, selectedInvestidor]);

  const getInvestidores = useCallback(async (instrumentoFinanceiroId: string) => {
    try {
      const result = await getInvestidoresApi(dispatch, instrumentoFinanceiroId, true);
      setInvestidoresAtivos(result.filter((i) => i.ativo === true) ?? []);
      setInvestidoresOfertaPrimaria(result.filter((i) => i.ativo === false) ?? []);
    } catch (error) { }
  }, []);

  const getAllInvestidores = useCallback(async () => {
    try {
        const result = usuario.participante.funcao === FuncaoParticipante.Escriturador ? await getInvestidoresEscrituradorApi(dispatch, undefined, true) : await investidoresList(dispatch, undefined, true);
        setAllInvestidores(result ?? []);
    }
    catch (error) { }
  }, []);

  const debouncedSearch = useCallback(
    debounce(async (cpfCnpjValue: string) => {
      const cpfCnpj = cpfCnpjValue?.replace(/\D/g, '');
      if (cpfCnpj && cpfCnpj.length >= 11) {
        const investidores = await getInvestidoresEscrituradorApi(dispatch, { cpfCnpj: cpfCnpj }, true);

        if (investidores.length > 0) {
          setSelectedInvestidor(investidores.find((i) => i.cpfCnpj === cpfCnpj) || null);
        } else {
          setSelectedInvestidor(null);
          dispatch(addAlert({
            message: 'Investidor não encontrado com o CPF/CNPJ informado.',
            severity: 'warning'
          }));
        }

        setInvestidorCpfCnpj(cpfCnpjValue);
      }
    }, 300),
    [dispatch]
  );

  const handleCpfCnpjChange = (value: string) => {
    setLocalCpfCnpj(value);
    debouncedSearch(value);
  };

  const renderInvestidorInput = (tipoCertidao: TipoCertidao) => {
    const isEscriturador = usuario?.participante.funcao === FuncaoParticipante.Escriturador;
    const shouldShowInvestidorField = tipoCertidao === TipoCertidao.ExtratoMovimentacoes || tipoCertidao === TipoCertidao.CertidaoTitularidade;

    if (!shouldShowInvestidorField) return null;

    // if (isEscriturador) {
    //   return (
    //     <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', width: '100%' }}>
    //       <TextField
    //         label="CPF/CNPJ Investidor"
    //         value={CpfCnpjMask(localCpfCnpj)}
    //         onChange={handleCpfCnpjChange}
    //         disabled={!tipo}
    //       />
    //     </div>
    //   );
    // }

    return (
      <InvestidorAutoComplete
        label="CPF/CNPJ Investidor"
        investidores={allInvestidores}
        investidor={selectedInvestidor}
        onChange={(value: InvestidorResponse | null) => {
          setSelectedInvestidor(value);
          if (tipoCertidao === TipoCertidao.ExtratoMovimentacoes) {
            setInvestidorId(value?.id ?? "");
          }
        }}
        disabled={!tipo}
      />
    );
  };

  const getAttachmentLink = async (attachmentLink: string) => {
    try {
      if ((instrumento && tipo === TipoCertidao.CertidaoTitularidade && investidorId) || (instrumento && tipo === TipoCertidao.TermoEncerramento)) {
        const link = document.createElement('a');

        link.href = attachmentLink;
        link.target = '_blank';
        link.download = `${tipo.toString()}_${instrumento.codigo}_${instrumento.custodiantes?.flatMap((custodiante) => {
          return custodiante.investidoresPrivados
        }).find((i) => i.investidorId === investidorId)?.razaoSocial.replaceAll(' ', '_')
          }`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
    }
  };

  const handleDownload = async () => {
    let link = '';

    if (tipo !== TipoCertidao.TermoEncerramento) {
      return;
    }
    try {
      if (!instrumento || !tipo) return;

      link = await getTermoEncerramento(dispatch, instrumento.instrumentoFinanceiroGuid, true);

      if (link.trim()) getAttachmentLink(link);

    } catch (error) { }
  };

  function onSelected(selected: Array<any>) {
    setListSelected(selected);
    if (selected.length === 0) {
      setBotaoAtivarOuInativar(false);
      return;
    }
    setBotaoAtivarOuInativar(true);
  }

  async function handleEmitirCertidao() {
    if (tipo === TipoCertidao.CertidaoTitularidade) {
      await postCertidaoTitularidade(dispatch, selectedInvestidor!.cpfCnpj, { codigosInstrumentos: listSelected, dataReferencia: dataReferencia! }, true);
    }
    if (tipo === TipoCertidao.Gravame) {
      await postCertidaoGravame(dispatch, listSelected, true);
    }
    if (tipo === TipoCertidao.CertidaoInadimplencia) {
      const inadimplenciaParams: CertidaoInadimplenciaParams = {
        emissorId: emissorId,
        codigosInstrumentos: listSelected,
      };
      await postCertidaoInadimplencia(dispatch, inadimplenciaParams, true);
    }
  }
  const handleChangeInstrumento = (selectedInstrumento: InstrumentoFinanceiroApiResponse | null) => {
    setInstrumento(selectedInstrumento);
    if (selectedInstrumento) getInvestidores(selectedInstrumento.instrumentoFinanceiroGuid);
  };

  useEffect(() => {
    if (usuario.participante.funcao != FuncaoParticipante.Emissor && emissorId != null) {
      getInstrumentos(emissorId);
    }
    if (usuario.participante.funcao === FuncaoParticipante.Emissor) {
      getInstrumentos(usuario.participante.id)
    }
    if (tipo === TipoCertidao.CertidaoTitularidade) {
      getInvestidorInstrumentos();
    }
  }, [getInvestidorInstrumentos, getInstrumentos, page, pageSize, instrumentosFinanceirosFilter, tipo]);


  const listaInvestidores = (ativos: boolean) => {
    return (ativos ? investidoresAtivos : investidoresOfertaPrimaria).length > 0 ? (
      (ativos ? investidoresAtivos : investidoresOfertaPrimaria).map((invest) => {
        return (
          <>
            <div
              style={{
                width: '100%',
                height: '50px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 30px',
                border: '1px solid #e2e2e2',
                borderRadius: '4px',
              }}
            >
              {!ativos ? (
                <Radio
                  style={{ width: '30px' }}
                  checked={invest.investidorId === investidorId ? true : false}
                  onChange={() => {
                    handleChangeInvestidor(invest);
                  }}
                  color="primary"
                  disabled={false}
                />
              ) : (
                <></>
              )}
              <Typography variant="body1" style={{ width: '350px' }}>
                {invest.razaoSocial}
              </Typography>
              <Typography variant="body2" style={{ width: '280px' }}>
                {addCpfCnpjMask(invest.cpfCnpj)}
              </Typography>
              <Typography variant="body2" style={{ width: '270px' }}>
                {invest.bancoInvestidor}
              </Typography>
              <MoreOptionsMenu>
                <MenuItem onClick={() => history.push(`/gerenciar/investidores/detalhes/${invest.investidorId}`)}>
                  <DescriptionIcon fontSize="small" style={{ marginRight: '10px' }} />
                  {'Ver Detalhes'}
                </MenuItem>
              </MoreOptionsMenu>
            </div>
            <SmSpace />
          </>
        );
      })
    ) : (
      <Typography
        variant="body2"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50px',
          border: '1px solid #e2e2e2',
          borderRadius: '4px',
          fontStyle: 'italic',
        }}
      >
        {'Não há investidores vinculados!'}
      </Typography>
    );
  };

  return (
    <div style={{ marginRight: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h1">{'Certidões'}</Typography>
      </div>
      <LgSpace />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'flex-start', width: '75%' }}>
          <Dropdown
            label={'Tipo'}
            required
            options={
              usuario.participante.funcao === FuncaoParticipante.Emissor ?
                tipoCertidaoOptions.filter(opt => opt.value === TipoCertidao.CertidaoInadimplencia)
                : tipoCertidaoOptions
            }
            value={tipo ?? ''}
            onChange={handleChangeTipo}
          />

          {tipo === TipoCertidao.TermoEncerramento && (
            <InstrumentoAutocomplete
              label={'Instrumento Financeiro *'}
              instrumentos={instrumentos}
              instrumento={instrumento}
              onChange={handleChangeInstrumento}
              disabled={!tipo}
            />
          )}

          {renderInvestidorInput(tipo!)}

          {tipo === TipoCertidao.CertidaoInadimplencia && usuario?.participante.funcao != FuncaoParticipante.Emissor && (
            <ParticipanteAutoComplete
              label={`Emissor *`}
              participantes={emissores}
              participante={emissor}
              onChange={handleEmissorChange}
            />
          )}

          {tipo === TipoCertidao.CertidaoTitularidade && (
            <DatePicker
              required
              label="Data de Referência"
              value={dataReferencia}
              onChange={debounce((value) => {
                if (!value || (dataReferencia && !!value?.isSame(dataReferencia))) {
                  return;
                }
                return setDataReferencia(value?.clone());
              }, 600)}
              maxDate={moment()}
            />
          )}
        </div>

        <SmSpace />

        {tipo === TipoCertidao.ExtratoMovimentacoes && selectedInvestidor && (
          <DetalhesInvestidor investidor={selectedInvestidor} />
        )}

        {tipo === TipoCertidao.CertidaoTitularidade && (
          <div style={{ marginRight: 50 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={'contained'}
                color="primary"
                size="medium"
                onClick={handleEmitirCertidao}
                disabled={!botaoAtivar}
              >
                {'Emitir Certidão'}
              </Button>
            </div>
            <Grid container justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
              <Typography variant="h6">{'Instrumentos Financeiros'}</Typography>
            </Grid>
            <SmSpace></SmSpace>
            <>
              <div style={{ height: 500, width: '90vw' }}>
                <DataGridPro
                  checkboxSelection
                  onSelectionModelChange={(newSelectionModel) => {
                    onSelected(newSelectionModel);
                  }}
                  localeText={localizedTextsMap}
                  rows={(investidorInstrumentos ?? []).map((instrumento) => ({ ...instrumento, id: instrumento.codigo }))}
                  columns={titularidadeGridColumns}
                  disableSelectionOnClick
                  rowsPerPageOptions={rowsPerPageOptionsValues}
                  pagination
                  autoPageSize
                />
              </div>
            </>
          </div>
        )}

        {tipo === TipoCertidao.CertidaoInadimplencia && (
          <div style={{ marginRight: 50 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={'contained'}
                color="primary"
                size="medium"
                onClick={handleEmitirCertidao}
                disabled={!botaoAtivar}
              >
                {'Emitir Certidão'}
              </Button>
            </div>
            <Grid container justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
              <Typography variant="h6">{'Instrumentos Financeiros'}</Typography>
            </Grid>
            <SmSpace></SmSpace>
            <>
              <div style={{ height: 500, width: '90vw' }}>
                <DataGridPro
                  checkboxSelection
                  onSelectionModelChange={(newSelectionModel) => {
                    onSelected(newSelectionModel);
                  }}
                  localeText={localizedTextsMap}
                  rows={(instrumentos ?? []).map((instrumento) => ({ ...instrumento, id: instrumento.codigo }))}
                  columns={inadimplenciaColumns}
                  disableSelectionOnClick
                  rowsPerPageOptions={rowsPerPageOptionsValues}
                  page={page}
                  rowCount={pageCount}
                  pagination
                  paginationMode="server"
                  onPageChange={(newPage) => setPage(newPage)}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize), setPage(0);
                  }}
                  pageSize={pageSize}
                />
              </div>
            </>
          </div>
        )}

        {bloqueios.length > 0 && tipo === TipoCertidao.Gravame && (
          <div style={{ marginRight: 50 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={'contained'}
                color="primary"
                size="medium"
                onClick={handleEmitirCertidao}
                disabled={!botaoAtivar}
              >
                {'Emitir Certidão'}
              </Button>
            </div>
            <Grid container justifyContent={'space-between'} direction={'row'} alignItems={'center'}>
              <Typography variant="h6">{'Bloqueios do instrumento Financeiro'}</Typography>
            </Grid>
            <SmSpace></SmSpace>
            <>
              <div style={{ height: 500, width: '90vw' }}>
                <DataGridPro
                  checkboxSelection
                  onSelectionModelChange={(newSelectionModel) => {
                    onSelected(newSelectionModel);
                  }}
                  localeText={localizedTextsMap}
                  rows={bloqueios.map((bloqueio) => ({ ...bloqueio, id: bloqueio.id }))}
                  columns={gravameGridColumns}
                  disableSelectionOnClick
                  rowsPerPageOptions={rowsPerPageOptionsValues}
                  pagination
                  autoPageSize
                />
              </div>
            </>
          </div>
        )}

        {instrumento && tipo !== TipoCertidao.Gravame && tipo && (
          <div style={{ width: '100%' }}>
            <Section title={'Instrumento Financeiro'} defaultGrid>
              <div>
                <Typography variant="body2">{'Data da Emissão'}</Typography>
                <Typography variant="body1">
                  {moment(instrumento.criteriosRemuneracao.dataEmissao).format('DD/MM/YYYY')}
                </Typography>
              </div>
              <div>
                <Typography variant="body2">{'Número da Emissão'}</Typography>
                <Typography variant="body1">{instrumento.numeroEmissao}</Typography>
              </div>
              <div>
                <Typography variant="body2">{'Emissor'}</Typography>
                <Typography variant="body1">{instrumento.razaoSocialEmissor}</Typography>
              </div>
              <div>
                <Typography variant="body2">{'Valor da Emissão:'}</Typography>
                <Typography variant="body1">
                  {formatInstrumentoCurrency(instrumento.criteriosRemuneracao.valorNominalUnitarioEmissao, instrumento.tipoInstrumentoFinanceiro)}
                </Typography>
              </div>
              <div>
                <Typography variant="body2">{'Tipo do Instrumento'}</Typography>
                <Typography variant="body1">
                  {getInstrumentoFinanceiroShortDescription[instrumento.tipoInstrumentoFinanceiro]}
                </Typography>
              </div>
              <div>
                <Typography variant="body2">{'Código do Ativo'}</Typography>
                <Typography variant="body1">{instrumento.codigo}</Typography>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                {tipo === TipoCertidao.TermoEncerramento && (
                  <Button
                    variant={'contained'}
                    color="primary"
                    size="medium"
                    onClick={handleDownload}
                    disabled={instrumento.id ? false : true}
                  >
                    {'Baixar Termo'}
                  </Button>
                )}
              </div>
            </Section>
            {!(tipo === TipoCertidao.TermoEncerramento) && (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                  <Typography variant="h3">{'Investidores Ativos'}</Typography>
                </div>
                <SmSpace />
                {listaInvestidores(true)}
                <SmSpace />
                <div
                  style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="h3">{'Investidores Oferta Primária'}</Typography>
                  <Button
                    variant={'contained'}
                    color="primary"
                    size="medium"
                    onClick={handleDownload}
                    disabled={investidorId ? false : true}
                  >
                    {'Baixar Certidão'}
                  </Button>
                </div>
                <SmSpace />
                {listaInvestidores(false)}
              </>
            )}
          </div>
        )}
        <LgSpace />
      </div>
    </div>


  );

};

export default Certidoes;
